/**
 * Layer 3: enum catalog hooks.
 *
 * `useEnums` exposes the whole localized catalog; `useEnumList` pulls one list
 * by its backend (snake_case) name and is the building block every dropdown
 * uses. Labels come pre-localized from the API, so consumers never call `t()`
 * on enum values — they render `item.label` directly.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getEnumsQueryOptions } from "@/shared/lib/enumsQueryOptions";
import type { EnumItem } from "@/shared/lib/enumsApi";

/** Returns the full enum catalog query (data, isLoading, isError, …). */
export function useEnums() {
  return useQuery(getEnumsQueryOptions());
}

/** Convert a snake_case enum name (`age_group`) to the camelCase response key (`ageGroup`). */
function toCamel(name: string): string {
  return name.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

export interface UseEnumListResult {
  items: EnumItem[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Returns one localized choice list by its backend name.
 *
 * Accepts the snake_case name as the backend defines it (`"contributor_role"`,
 * `"binding_type"`, …) and resolves it against the camelCased response keys.
 * Falls back to an empty list while loading or when the name is unknown — a
 * loading dropdown should render disabled with no options, never crash.
 */
export function useEnumList(name: string): UseEnumListResult {
  const { data, isLoading, isError } = useEnums();
  const items = data?.[name] ?? data?.[toCamel(name)] ?? [];
  return { items, isLoading, isError };
}

/**
 * Returns a resolver mapping a value of enum `name` to its localized label.
 *
 * For read views (detail pages, tables) that render stored enum values: the
 * resolver looks the value up in the localized catalog and falls back to the
 * raw value for unknown/legacy entries, so a cell is never blank.
 */
export function useEnumLabeller(name: string): (value: string) => string {
  const { items } = useEnumList(name);
  return useMemo(() => {
    const map = new Map(items.map((i) => [i.value, i.label]));
    return (value: string) => map.get(value) ?? value;
  }, [items]);
}
